import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Select, { SelectOption } from "@amzn/meridian/select";
import React, { useEffect, useState } from "react";
import {
  getConfirmedAnswer,
  getLabel,
  isAnswerInadequate,
  isAnswerMatch,
  isFieldConfirmedAnswerValid,
  isFieldMultiSelected,
  isAssignedBasedReview,
} from "src/components/shared/groundTruth/reviewTable/helpers";
import {
  GroundTruthField,
  ReviewResult,
  setFieldConfirmedAnswer,
} from "src/store/modelLifeCycleGroundTruthSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";
import FullOptionList from "./fullOptionList/fullOptionList";
import "./reviewDecisionCells.scss";

interface ReviewDecisionProps {
  randomizedOrder: boolean;
  groundTruthField: GroundTruthField;
  fieldIndex: number;
}

const ReviewDecisionCells: React.FC<ReviewDecisionProps> = ({
  randomizedOrder,
  groundTruthField,
  fieldIndex,
}) => {
  const dispatch = useAppDispatch();
  const { selectedModel } = useAppSelector((state) => state.modelSlice);
  const {
    selectedGroundTruthData,
    isBlind,
    showLLMAnswerColumn,
    shouldShowError,
    showUserAnswerColumn,
  } = useAppSelector((state) => state.modelLifeCycleGroundTruthSlice);

  const [showFullOptionList, setShowFullOptionList] = useState(
    (!showUserAnswerColumn && !showLLMAnswerColumn) ||
      isAssignedBasedReview(selectedModel) ||
      groundTruthField.reviewResult === ReviewResult.NEITHER,
  );

  useEffect(() => {
    setShowFullOptionList(
      (!showUserAnswerColumn && !showLLMAnswerColumn) ||
        isAssignedBasedReview(selectedModel) ||
        groundTruthField.reviewResult === ReviewResult.NEITHER,
    );
  }, [showUserAnswerColumn, showLLMAnswerColumn, groundTruthField.reviewResult, selectedModel]);

  const getValue = (reviewResult: ReviewResult) => {
    if (reviewResult === ReviewResult.BOTH_CORRECT) return ReviewResult.BOTH_CORRECT;
    if (reviewResult === ReviewResult.NEITHER) return ReviewResult.NEITHER;

    // Convert array back to pipe-delimited string for Select component
    // This ensures the value matches the SelectOption values (llmAnswer, userAnswer)
    if (Array.isArray(groundTruthField.confirmedAnswer)) {
      return groundTruthField.confirmedAnswer.join("|");
    }

    return groundTruthField.confirmedAnswer;
  };

  return (
    <>
      <Row>
        <Column>
          {!isAssignedBasedReview(selectedModel) &&
            (showUserAnswerColumn || showLLMAnswerColumn) && (
              <Select
                width="200px"
                size={"small"}
                value={getValue(groundTruthField.reviewResult)}
                onChange={(value) => {
                  if (value === ReviewResult.NEITHER) {
                    setShowFullOptionList(true);
                  } else {
                    setShowFullOptionList(false);
                  }
                  dispatch(
                    setFieldConfirmedAnswer({
                      fieldIndex: fieldIndex,
                      confirmedAnswer: getConfirmedAnswer(
                        groundTruthField,
                        value,
                        isFieldMultiSelected(groundTruthField, selectedModel),
                      ),
                      reviewResult: value,
                    }),
                  );
                }}
                placeholder="Choose output..."
                error={
                  shouldShowError && !isFieldConfirmedAnswerValid(groundTruthField.confirmedAnswer)
                }
                errorMessage={
                  shouldShowError &&
                  !isFieldConfirmedAnswerValid(groundTruthField.confirmedAnswer) &&
                  "please select an answer"
                }
              >
                {!isBlind && !isAnswerInadequate(groundTruthField.llmAnswer) && (
                  <SelectOption
                    key={groundTruthField.llmAnswer}
                    value={groundTruthField.llmAnswer}
                    label={getLabel(groundTruthField, ReviewResult.LLM_CORRECT)}
                  />
                )}
                {!isBlind &&
                  !isAnswerMatch(
                    groundTruthField,
                    isFieldMultiSelected(groundTruthField, selectedModel),
                  ) &&
                  !isAnswerInadequate(groundTruthField.userAnswer) && (
                    <SelectOption
                      key={groundTruthField.userAnswer}
                      value={groundTruthField.userAnswer}
                      label={getLabel(groundTruthField, ReviewResult.HUMAN_CORRECT)}
                    />
                  )}
                {isBlind && randomizedOrder && !isAnswerInadequate(groundTruthField.llmAnswer) && (
                  <SelectOption
                    key={groundTruthField.llmAnswer}
                    value={groundTruthField.llmAnswer}
                    label={getLabel(groundTruthField, ReviewResult.LLM_CORRECT)}
                  />
                )}
                {isBlind &&
                  randomizedOrder &&
                  !isAnswerMatch(
                    groundTruthField,
                    isFieldMultiSelected(groundTruthField, selectedModel),
                  ) &&
                  !isAnswerInadequate(groundTruthField.userAnswer) && (
                    <SelectOption
                      key={groundTruthField.userAnswer}
                      value={groundTruthField.userAnswer}
                      label={getLabel(groundTruthField, ReviewResult.HUMAN_CORRECT)}
                    />
                  )}
                {isBlind &&
                  !randomizedOrder &&
                  !isAnswerMatch(
                    groundTruthField,
                    isFieldMultiSelected(groundTruthField, selectedModel),
                  ) &&
                  !isAnswerInadequate(groundTruthField.userAnswer) && (
                    <SelectOption
                      key={groundTruthField.userAnswer}
                      value={groundTruthField.userAnswer}
                      label={getLabel(groundTruthField, ReviewResult.HUMAN_CORRECT)}
                    />
                  )}
                {isBlind && !randomizedOrder && !isAnswerInadequate(groundTruthField.llmAnswer) && (
                  <SelectOption
                    key={groundTruthField.llmAnswer}
                    value={groundTruthField.llmAnswer}
                    label={getLabel(groundTruthField, ReviewResult.LLM_CORRECT)}
                  />
                )}
                {!isAnswerInadequate(groundTruthField.llmAnswer) &&
                  !isAnswerInadequate(groundTruthField.userAnswer) &&
                  groundTruthField.llmAnswer !== groundTruthField.userAnswer && (
                    <SelectOption
                      key={ReviewResult.BOTH_CORRECT}
                      value={ReviewResult.BOTH_CORRECT}
                      label={getLabel(groundTruthField, ReviewResult.BOTH_CORRECT)}
                    />
                  )}
                <SelectOption
                  key={ReviewResult.INADEQUATE}
                  value={ReviewResult.INADEQUATE}
                  label={getLabel(groundTruthField, ReviewResult.INADEQUATE)}
                />
                <SelectOption
                  key={ReviewResult.NEITHER}
                  value={ReviewResult.NEITHER}
                  label={getLabel(groundTruthField, ReviewResult.NEITHER)}
                />
              </Select>
            )}
          {showFullOptionList && (
            <FullOptionList
              selectedAuditData={selectedGroundTruthData}
              auditField={groundTruthField}
              fieldIndex={fieldIndex}
            />
          )}
        </Column>
      </Row>
    </>
  );
};

export default ReviewDecisionCells;
